import React, { memo, useState, useEffect, useCallback } from 'react';
import { NoResults } from '~/features/productsList/components';
import { AlgoliaClientSearch } from '~/features/productsList/components';
import DynamicFilters from '~/features/productsList/components/filters/dynamic-filters/dynamic-filters';
import { M170SearchResultsModule } from '~/lib/data-contract';
import {
    StyledProductList,
    StyledCategoriesContainer,
    StyledProductListContainer,
} from '../M140ProductsList';
import { SearchBox, useInstantSearch } from 'react-instantsearch';
import { useFrame } from '~/shared/utils';
import { CategoriesFilters } from './components';
import { VisuallyHidden } from '~/shared/components/VisuallyHidden';
import { useMedia } from 'react-use';
import { queries } from '~/theme/breakpoints';
import { ProductsGrid } from '~/features/productsList/components/products-grid/products-grid.component';
import { Container } from '../container/container.component';
import { algoliaCache } from '~/features/productsList/components/AlgoliaCache';
import ProductListSkeleton from '~/features/productsList/product-list-skeleton/product-list-skeleton';

// Signals parent when InstantSearch has finished its first fetch
const AlgoliaReadySignal = ({ onReady }: { onReady: () => void }) => {
    const { status } = useInstantSearch();
    useEffect(() => {
        if (status === 'idle') onReady();
    }, [status, onReady]);
    return null;
};

export type M170SearchProps = M170SearchResultsModule;

export const M170Search = memo(
    ({ itemsPerPage, noResultsCategories, noResultsContent }: M170SearchProps) => {
        const { data: frameData } = useFrame();
        const isDesktop = useMedia(queries.md, false);
        const hitsPerPage = itemsPerPage ?? 24;

        // Track client-side mount — skeleton shown on server and during hydration
        const [mounted, setMounted] = useState(false);
        useEffect(() => setMounted(true), []);

        // Tracks when Algolia has returned its first result set — prevents blank gap (CLS)
        const [algoliaReady, setAlgoliaReady] = useState(false);
        // showSkeleton trails algoliaReady by two rAFs — skeleton display:none + Algolia visible
        // painted atomically, then skeleton node unmounted after browser has already painted
        const [showSkeleton, setShowSkeleton] = useState(true);
        const handleAlgoliaReady = useCallback(() => {
            setAlgoliaReady(true);
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setShowSkeleton(false))
            );
        }, []);

        return (
            <Container spacingTop="none" hasGutter>
                {showSkeleton && (
                    <div style={algoliaReady ? { display: 'none' } : undefined}>
                        <ProductListSkeleton
                            hitsPerPage={hitsPerPage}
                            isSearchPage
                            gridLayout="with-menu"
                        />
                    </div>
                )}
                {mounted && (
                    <div style={algoliaReady ? undefined : { display: 'none' }}>
                        <AlgoliaClientSearch
                            hitsPerPage={hitsPerPage}
                            algoliaApp={frameData?.search?.algolia?.appKey || ''}
                            algoliaToken={frameData?.search?.algolia?.apiToken || ''}
                        >
                            <AlgoliaReadySignal onReady={handleAlgoliaReady} />
                            <NoResults
                                categories={noResultsCategories}
                                content={noResultsContent}
                                type="search"
                            >
                                <StyledProductList isSearch withMenu>
                                    <StyledCategoriesContainer isSearch>
                                        <CategoriesFilters
                                            attribute={frameData?.search?.algolia?.categoriesKey || ''}
                                            limit={isDesktop ? 10 : 4}
                                        />
                                    </StyledCategoriesContainer>
                                    <StyledProductListContainer>
                                        <DynamicFilters facets={['*']} />
                                        <VisuallyHidden>
                                            <SearchBox />
                                        </VisuallyHidden>
                                        <ProductsGrid
                                            cache={algoliaCache}
                                            hitsPerPage={hitsPerPage}
                                            isSearchPage
                                        />
                                    </StyledProductListContainer>
                                </StyledProductList>
                            </NoResults>
                        </AlgoliaClientSearch>
                    </div>
                )}
            </Container>
        );
    }
);
